import { useSyncExternalStore } from "react";
import { read } from "../net/rally.ts";
import { presentAmong, type SeenHere, sameNames } from "./present.ts";

export const ASK_WHO_IS_HERE_EVERY = 15_000;

type WhoIsHere = { seen: SeenHere[]; forSeconds: number };

let heard: SeenHere[] = [];
let forSeconds = 0;

let here: ReadonlySet<string> = new Set<string>();
const listeners = new Set<() => void>();
let beat: ReturnType<typeof setInterval> | null = null;
let watching = false;

function settle(): void {
  const next = presentAmong(heard, Date.now(), forSeconds > 0 ? forSeconds * 1000 : undefined);
  if (sameNames(next, here)) return;
  here = next;
  for (const listener of [...listeners]) listener();
}

function ask(): void {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    settle();
    return;
  }
  void read<WhoIsHere>("presence.here")
    .then((said) => {
      heard = said.seen;
      forSeconds = said.forSeconds;
      settle();
    })

    .catch(() => settle());
}

function watch(): void {
  if (watching) return;
  watching = true;
  ask();
  beat = setInterval(ask, ASK_WHO_IS_HERE_EVERY);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", ask);
}

function stopIfNobodyIsListening(): void {
  if (listeners.size > 0 || !watching) return;
  watching = false;
  if (beat !== null) clearInterval(beat);
  beat = null;
  if (typeof document !== "undefined") document.removeEventListener("visibilitychange", ask);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  watch();
  return () => {
    listeners.delete(listener);
    stopIfNobodyIsListening();
  };
}

export function usePresent(account: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => (account ? here.has(account) : false),
    () => false,
  );
}
